import type { CSSProperties, ImgHTMLAttributes } from "react";
import { Picture } from "@/components/Picture";
import type { ManifestEntry } from "@/lib/car-image-manifest";
import { supabaseImageUrl, supabaseImageSrcSet } from "@/lib/supabase-image";

/**
 * `<CarPicture>` — render a car listing photo with the best available
 * delivery for the given URL.
 *
 *   - When a manifest entry is supplied (uploaded via the new admin
 *     pipeline): emits a full `<picture>` with AVIF + WebP + JPG sources
 *     across 960/1280/1920 widths.
 *   - When no manifest exists (legacy uploads, Bilinfo CDN URLs):
 *     falls back to a single `<img>` with the existing Supabase image
 *     transform srcset (1x/2x), which is what `CarCard` was using before.
 *
 * Callers don't need to know which path is taken — they pass `manifest`
 * if they have it and `src` always, and the component picks the best
 * renderer. Identical sizing/loading attributes flow through both paths
 * so layout is stable regardless of coverage.
 */
export interface CarPictureProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    "src" | "srcSet" | "sizes" | "loading" | "decoding" | "fetchPriority"
  > {
  /** The URL stored in `cars.images[i]`. Used as both lookup key and fallback `src`. */
  src: string;
  /** Optional manifest entry — present when the upload went through the new pipeline. */
  manifest?: ManifestEntry;
  alt: string;
  /** Intrinsic width — required to prevent CLS. Falls back to manifest width. */
  width?: number;
  /** Intrinsic height — required to prevent CLS. */
  height?: number;
  /** Same `sizes` attribute applied to manifest sources and to the fallback `<img>`. */
  sizes?: string;
  /** Mark as LCP (skips lazy-loading + sets fetchpriority=high). */
  priority?: boolean;
  /**
   * Card-target width in CSS px. Used by the legacy fallback to ask
   * Supabase image-transform for 1x/2x variants of the right size. Ignored
   * when a manifest is present (manifest already ships 960/1280/1920).
   */
  fallbackBaseWidth?: number;
  /** Fallback JPEG quality 20–100 when transformed via Supabase image-transform. */
  fallbackQuality?: number;
  pictureStyle?: CSSProperties;
  pictureClassName?: string;
}

export function CarPicture({
  src,
  manifest,
  alt,
  width,
  height,
  sizes = "100vw",
  priority,
  fallbackBaseWidth = 800,
  fallbackQuality = 80,
  pictureStyle,
  pictureClassName,
  ...imgProps
}: CarPictureProps) {
  // ---- Manifest path: full <picture> with AVIF/WebP/JPG sources ---------
  if (manifest) {
    return (
      <Picture
        sources={manifest.sources}
        fallback={manifest.fallback}
        alt={alt}
        width={width ?? manifest.width}
        height={height ?? manifest.height}
        sizes={sizes}
        priority={priority}
        pictureStyle={pictureStyle}
        pictureClassName={pictureClassName}
        {...imgProps}
      />
    );
  }

  // ---- Fallback path: legacy single <img> + Supabase transform srcset ---
  // Mirrors what `CarCard` used pre-manifest so listing density stays
  // identical. `supabaseImageUrl` is a no-op for non-Supabase URLs (e.g.
  // Bilinfo CDN), in which case we just render the original src.
  const transformedSrc = supabaseImageUrl(src, { width: fallbackBaseWidth, quality: fallbackQuality });
  const srcSet = supabaseImageSrcSet(src, fallbackBaseWidth, { quality: fallbackQuality });
  const loading = priority ? undefined : "lazy";

  return (
    <img
      {...imgProps}
      src={transformedSrc || src}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      fetchPriority={priority ? "high" : "auto"}
    />
  );
}
